import { supabase } from '../supabaseClient'

export const bploService = {
  // Business Tax Settings
  async getBusinessTaxSettings() {
    try {
      const { data, error } = await supabase
        .from('bplo_business_tax_settings')
        .select('*')
        .order('category', { ascending: true })
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async createBusinessTaxSetting(setting) {
    try {
      const { data, error } = await supabase
        .from('bplo_business_tax_settings')
        .insert(setting)
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async updateBusinessTaxSetting(id, setting) {
    try {
      const { data, error } = await supabase
        .from('bplo_business_tax_settings')
        .update(setting)
        .eq('id', id)
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteBusinessTaxSetting(id) {
    try {
      const { error } = await supabase
        .from('bplo_business_tax_settings')
        .delete()
        .eq('id', id)
      if (error) throw error
      
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async getApplicants() {
    try {
      const { data, error } = await supabase
        .from('bplo_applicants')
        .select('*')
        .order('name', { ascending: true })
        .limit(10000)
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching applicants:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getApplicantsByIds(ids) {
    try {
      if (!ids || ids.length === 0) return { success: true, data: [] };
      const { data, error } = await supabase
        .from('bplo_applicants')
        .select('*')
        .in('id', ids);
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching applicants by IDs:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  async createApplicant(applicant) {
    try {
      const payload = { 
        ...applicant, 
        status: 'Active', 
        date_registered: new Date().toISOString().slice(0, 10) 
      }
      const { data, error } = await supabase
        .from('bplo_applicants')
        .insert(payload)
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      console.error('Error creating applicant:', error)
      return { success: false, error: error.message }
    }
  },

  async updateApplicant(id, applicant) {
    try {
      const { data, error } = await supabase
        .from('bplo_applicants')
        .update(applicant)
        .eq('id', id)
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      console.error('Error updating applicant:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteApplicant(id) {
    try {
      const { error } = await supabase
        .from('bplo_applicants')
        .delete()
        .eq('id', id)
      if (error) throw error
      
      return { success: true }
    } catch (error) {
      console.error('Error deleting applicant:', error)
      return { success: false, error: error.message }
    }
  }
  ,
  async getNatureOfBusiness() {
    try {
      const { data, error } = await supabase
        .from('bplo_nature_of_business')
        .select('*')
        .order('name', { ascending: true })
        .limit(10000)
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async getOccupations() {
    try {
      const { data, error } = await supabase
        .from('occupations')
        .select('*')
        .order('name', { ascending: true })
        .limit(10000)
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async createOccupation(name) {
    try {
      const { data, error } = await supabase
        .from('occupations')
        .insert({ name })
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async updateOccupation(id, updates) {
    try {
      const { data, error } = await supabase
        .from('occupations')
        .update(updates)
        .eq('id', id)
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteOccupation(id) {
    try {
      const { error } = await supabase
        .from('occupations')
        .delete()
        .eq('id', id)
      if (error) throw error
      
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async checkOccupationUsage(id) {
    try {
      // Check in application_occupations junction table
      const { count, error } = await supabase
        .from('bplo_application_occupations')
        .select('id', { count: 'exact', head: true })
        .eq('occupation_id', id);
      
      if (error) throw error;
      
      if (count > 0) {
        return { inUse: true, reason: `Used in ${count} applications` };
      }
      
      return { inUse: false };
    } catch (error) {
      console.error('Check usage failed:', error);
      // If check fails, assume safe to delete but log error, or safer: assume in use to prevent accidental deletion on error
      // Let's assume safe but return error message if strict
      return { inUse: false }; 
    }
  },
  async getApplications() {
    try {
      const { data, error } = await supabase
        .from('bplo_applications_view')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10000)
      if (error) throw error

      // Fetch vehicles for Motorcycle/Tricycle permits separately as they might be missing in the view
      const { data: motoData, error: motoError } = await supabase
        .from('bplo_motorcycle_permits')
        .select('id, vehicles');
      
      if (!motoError && motoData) {
        const motoMap = new Map(motoData.map(m => [m.id, m.vehicles]));
        data.forEach(app => {
            if (app.application_type === 'Motorcycle and Tricycle Permit' && motoMap.has(app.id)) {
                app.vehicles = motoMap.get(app.id);
            }
        });
      }

      // Fetch fishing vessels and gears for Fishing permits separately
      const { data: fishData, error: fishError } = await supabase
        .from('bplo_fishing_permits')
        .select('id, fishing_vessels, fishing_gears');
      
      if (!fishError && fishData) {
        const fishMap = new Map(fishData.map(f => [f.id, { vessels: f.fishing_vessels, gears: f.fishing_gears }]));
        data.forEach(app => {
            if (app.application_type === 'Fishing Vessel and Gear Registration' && fishMap.has(app.id)) {
                const details = fishMap.get(app.id);
                app.fishing_vessels = details.vessels;
                app.fishing_gears = details.gears;
            }
        });
      }

      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async createApplication(app) {
    try {
      let table = 'bplo_applications';
      let payload = { ...app };
      
      // Sanitize dates
      if (payload.expiry_date === '') payload.expiry_date = null;
      if (payload.issue_date === '') payload.issue_date = null;

      if (app.application_type === 'Business Permit') {
        table = 'bplo_business_permits';
        const { 
          applicant_id, application_no, application_type, issue_date, expiry_date, status,
          type_of_business, business_name, business_address, nature_of_business, capital, gross, gross_breakdown 
        } = payload;
        payload = { 
          applicant_id, application_no, application_type, issue_date, expiry_date, status,
          type_of_business, business_name, business_address, nature_of_business, capital, gross, gross_breakdown 
        };
      } else if (app.application_type === 'Motorcycle and Tricycle Permit') {
        table = 'bplo_motorcycle_permits';
        const {
            applicant_id, application_no, application_type, issue_date, expiry_date, status,
            vehicle_type, service_type, brand_model, no_of_units, plate_number, sticker_number, license_number, license_expiry, billing_charges, vehicles
        } = payload;
        payload = {
            applicant_id, application_no, application_type, issue_date, expiry_date, status,
            vehicle_type, service_type, brand_model, no_of_units, plate_number, sticker_number, license_number, license_expiry, billing_charges, vehicles
        };
      } else if (app.application_type === 'Fishing Vessel and Gear Registration') {
        table = 'bplo_fishing_permits';
        const {
            applicant_id, application_no, application_type, issue_date, expiry_date, status,
            fishing_vessels, fishing_gears, billing_charges
        } = payload;
        payload = {
            applicant_id, application_no, application_type, issue_date, expiry_date, status,
            fishing_vessels, fishing_gears, billing_charges
        };
      } else if (app.application_type === 'Sanitary Permit') {
        table = 'bplo_sanitary_permits';
        const { 
          applicant_id, application_no, application_type, issue_date, expiry_date, status, 
          or_no, name, barangay, establishment_name 
        } = payload;
        payload = { 
          applicant_id, application_no, application_type, issue_date, expiry_date, status, 
          or_no, name, barangay, establishment_name 
        };
      } else if (app.application_type === 'Occupation/Calling') {
        table = 'bplo_occupation_permits';
        // Map occupations to occupations_json if not present
        if (payload.occupations && !payload.occupations_json) {
            payload.occupations_json = payload.occupations;
        }
        const {
          applicant_id, application_no, application_type, issue_date, expiry_date, status,
          occupations_json, billing_charges
        } = payload;
        payload = {
          applicant_id, application_no, application_type, issue_date, expiry_date, status,
          occupations_json, billing_charges
        };
      }

      const { data, error } = await supabase
        .from(table)
        .insert(payload)
        .select()
      if (error) throw error

      // Handle junction table for Occupation/Calling
      const occupationList = app.occupations || app.occupations_json;
      if (app.application_type === 'Occupation/Calling' && data?.[0]?.id && occupationList) {
        const junctionPayload = occupationList.map(occ => ({
          application_id: data[0].id,
          occupation_id: occ.id,
          created_by: app.created_by // if available
        }));
        if (junctionPayload.length > 0) {
          await supabase.from('bplo_application_occupations').insert(junctionPayload);
        }
      }

      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async updateApplication(id, app) {
    try {
      // Determine table based on application_type if present, otherwise we might need to search or pass it.
      // Ideally app object has application_type.
      let table = 'bplo_applications';
      let payload = { ...app };
      
      // Sanitize dates
      if (payload.expiry_date === '') payload.expiry_date = null;
      if (payload.issue_date === '') payload.issue_date = null;

      let type = app.application_type;
      if (!type) {
         // Try to find it in the view
         const { data: existing } = await supabase.from('bplo_applications_view').select('application_type').eq('id', id).single();
         if (existing) type = existing.application_type;
      }

      if (type === 'Business Permit') {
        table = 'bplo_business_permits';
        // Pick only relevant fields for update. 
        // Note: For update, we might only want to send fields that are present in `app`.
        // But since we are whitelisting, we need to be careful not to send `undefined` for missing fields if we want partial updates.
        // However, standardizing on sending known fields is safer than sending unknown fields.
        // We will construct a cleaned payload containing ONLY the known fields that exist in the input `app`.
        
        const allowed = [
          'applicant_id', 'application_no', 'application_type', 'issue_date', 'expiry_date', 'status',
          'type_of_business', 'business_name', 'business_address', 'nature_of_business', 'capital', 'gross', 'gross_breakdown'
        ];
        const cleaned = {};
        allowed.forEach(k => { if (payload[k] !== undefined) cleaned[k] = payload[k]; });
        payload = cleaned;

      } else if (type === 'Motorcycle and Tricycle Permit') {
        table = 'bplo_motorcycle_permits';
        const allowed = [
            'applicant_id', 'application_no', 'application_type', 'issue_date', 'expiry_date', 'status',
            'vehicle_type', 'service_type', 'brand_model', 'no_of_units', 'plate_number', 'sticker_number', 'license_number', 'license_expiry', 'billing_charges', 'vehicles'
        ];
        const cleaned = {};
        allowed.forEach(k => { if (payload[k] !== undefined) cleaned[k] = payload[k]; });
        payload = cleaned;

      } else if (type === 'Fishing Vessel and Gear Registration') {
        table = 'bplo_fishing_permits';
        const allowed = [
            'applicant_id', 'application_no', 'application_type', 'issue_date', 'expiry_date', 'status',
            'fishing_vessels', 'fishing_gears', 'billing_charges'
        ];
        const cleaned = {};
        allowed.forEach(k => { if (payload[k] !== undefined) cleaned[k] = payload[k]; });
        payload = cleaned;
      } else if (type === 'Sanitary Permit') {
        table = 'bplo_sanitary_permits';
        const allowed = [
            'applicant_id', 'application_no', 'application_type', 'issue_date', 'expiry_date', 'status',
            'or_no', 'name', 'barangay', 'establishment_name'
        ];
        const cleaned = {};
        allowed.forEach(k => { if (payload[k] !== undefined) cleaned[k] = payload[k]; });
        payload = cleaned;
      } else if (type === 'Occupation/Calling') {
        table = 'bplo_occupation_permits';
        // Map occupations to occupations_json if not present
        if (payload.occupations && !payload.occupations_json) {
            payload.occupations_json = payload.occupations;
        }
        const allowed = [
          'applicant_id', 'application_no', 'application_type', 'issue_date', 'expiry_date', 'status',
          'occupations_json', 'billing_charges'
        ];
        const cleaned = {};
        allowed.forEach(k => { if (payload[k] !== undefined) cleaned[k] = payload[k]; });
        payload = cleaned;
      }

      const { data, error } = await supabase
        .from(table)
        .update(payload)
        .eq('id', id)
        .select()
      if (error) throw error
      
      // Handle junction table for Occupation/Calling Update
      const occupationList = app.occupations || app.occupations_json;
      if (type === 'Occupation/Calling' && occupationList) {
         // Delete existing
         await supabase.from('bplo_application_occupations').delete().eq('application_id', id);
         
         // Insert new
         const junctionPayload = occupationList.map(occ => ({
            application_id: id,
            occupation_id: occ.id,
            created_by: app.created_by
         }));
         
         if (junctionPayload.length > 0) {
            await supabase.from('bplo_application_occupations').insert(junctionPayload);
         }
      }

      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteApplication(id) {
    try {
      // Try deleting from all tables. Since IDs are UUIDs, this is safe.
      const errors = [];

      // Delete payments first (to avoid FK constraints)
      const { error: e0 } = await supabase.from('bplo_payments').delete().eq('application_id', id);
      if (e0) errors.push('Payments: ' + e0.message);

      const { error: e1 } = await supabase.from('bplo_business_permits').delete().eq('id', id);
      if (e1) errors.push('Business: ' + e1.message);
      
      const { error: e2 } = await supabase.from('bplo_motorcycle_permits').delete().eq('id', id);
      if (e2) errors.push('Motorcycle: ' + e2.message);
      
      const { error: e3 } = await supabase.from('bplo_fishing_permits').delete().eq('id', id);
      if (e3) errors.push('Fishing: ' + e3.message);
      
      const { error: e4 } = await supabase.from('bplo_sanitary_permits').delete().eq('id', id);
      if (e4) errors.push('Sanitary: ' + e4.message);

      const { error: e5 } = await supabase.from('bplo_occupation_permits').delete().eq('id', id);
      if (e5) errors.push('Occupation: ' + e5.message);
      
      // Also delete from old table just in case (only if id is not UUID)
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      if (!isUUID) {
        const { error: e6 } = await supabase
          .from('bplo_applications')
          .delete()
          .eq('id', id)
        if (e6) errors.push('Applications: ' + e6.message);
      } else {
        // If it is a UUID, we can't delete from bplo_applications if it expects BigInt.
        // But if bplo_applications was migrated to UUID, then we should.
        // Given the error "invalid input syntax for type bigint", it expects BigInt.
        // So we skip this step for UUIDs.
      }

      if (errors.length > 0) {
        throw new Error(errors.join('; '));
      }

      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteApplicationsByApplicant(applicantId) {
    try {
      const errors = [];
      
      const { error: e0 } = await supabase.from('bplo_payments').delete().eq('applicant_id', applicantId);
      if (e0) errors.push('Payments: ' + e0.message);

      const { error: e1 } = await supabase.from('bplo_business_permits').delete().eq('applicant_id', applicantId);
      if (e1) errors.push('Business: ' + e1.message);

      const { error: e2 } = await supabase.from('bplo_motorcycle_permits').delete().eq('applicant_id', applicantId);
      if (e2) errors.push('Motorcycle: ' + e2.message);

      const { error: e3 } = await supabase.from('bplo_fishing_permits').delete().eq('applicant_id', applicantId);
      if (e3) errors.push('Fishing: ' + e3.message);

      const { error: e4 } = await supabase.from('bplo_sanitary_permits').delete().eq('applicant_id', applicantId);
      if (e4) errors.push('Sanitary: ' + e4.message);

      const { error: e5 } = await supabase.from('bplo_occupation_permits').delete().eq('applicant_id', applicantId);
      if (e5) errors.push('Occupation: ' + e5.message);
      
      const { error: e6 } = await supabase
        .from('bplo_applications')
        .delete()
        .eq('applicant_id', applicantId)
      if (e6) errors.push('Applications: ' + e6.message);

      if (errors.length > 0) throw new Error(errors.join('; '));

      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async getLatestApplicationNo(pattern) {
    try {
      const { data, error } = await supabase
        .from('bplo_applications_view')
        .select('application_no')
        .ilike('application_no', pattern)
        .order('application_no', { ascending: false })
        .limit(1)
      if (error) throw error
      return { success: true, data: data?.[0]?.application_no }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async generateNextApplicationNo(type) {
    try {
      if (!type) return { success: false, error: 'Type is required' };
      const prefix = type.substring(0, 3).toUpperCase();
      const year = new Date().getFullYear();
      const pattern = `${prefix}${year}-%`;
      
      const { success, data } = await this.getLatestApplicationNo(pattern);
      
      let nextSeq = 1;
      if (success && data) {
        const parts = data.split('-');
        if (parts.length === 2) {
          const seq = parseInt(parts[1], 10);
          if (!isNaN(seq)) nextSeq = seq + 1;
        }
      }
      
      const formatted = `${prefix}${year}-${String(nextSeq).padStart(4, '0')}`;
      return { success: true, data: formatted };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
  async getApplicationCount() {
    try {
      const { count, error } = await supabase
        .from('bplo_applications_view')
        .select('id', { count: 'exact', head: true })
      if (error) throw error
      return { success: true, count: count || 0 }
    } catch (error) {
      return { success: false, error: error.message, count: 0 }
    }
  },
  async getApplicationCountByApplicant(applicantId) {
    try {
      const { count, error } = await supabase
        .from('bplo_applications_view')
        .select('id', { count: 'exact', head: true })
        .eq('applicant_id', applicantId)
      if (error) throw error
      return { success: true, count: count || 0 }
    } catch (error) {
      return { success: false, error: error.message, count: 0 }
    }
  },
  async getPayments() {
    try {
      const { data, error } = await supabase
        .from('bplo_payments')
        .select('*')
        .order('payment_date', { ascending: false })
        .limit(10000)
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async createPayment(payment) {
    try {
      const { data, error } = await supabase
        .from('bplo_payments')
        .insert(payment)
        .select()
      if (error) throw error
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async updatePayment(id, payment) {
    try {
      const payload = {
        or_no: payment.or_no ?? null,
        payment_date: payment.payment_date,
        amount: payment.amount,
        notes: payment.notes ?? null
      }
      const { data, error } = await supabase
        .from('bplo_payments')
        .update(payload)
        .eq('id', id)
        .select()
      if (error) throw error
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async getPaymentsByApplication(applicationId) {
    try {
      const { data, error } = await supabase
        .from('bplo_payments')
        .select('*')
        .eq('application_id', applicationId)
        .order('payment_date', { ascending: false })
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async unpayApplication(applicationId) {
    try {
      const { error: delError } = await supabase
        .from('bplo_payments')
        .delete()
        .eq('application_id', applicationId)
      if (delError) throw delError
      const updateRes = await this.updateApplication(applicationId, { status: 'Unpaid' })
      if (!updateRes.success) throw new Error(updateRes.error || 'Failed to update application status')
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }
  ,
  async getApplicationTypes() {
    try {
      const { data, error } = await supabase
        .from('bplo_application_types')
        .select('*')
        .order('name', { ascending: true })
        .limit(10000)
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async getCharges() {
    try {
      const { data, error } = await supabase
        .from('bplo_charges')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10000)
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async getChargesByCriteria(application_type_id, nature_of_business_id = null) {
    try {
      let query = supabase
        .from('bplo_charges')
        .select('*')
        .eq('application_type_id', application_type_id);

      if (nature_of_business_id) {
        query = query.eq('nature_of_business_id', nature_of_business_id);
      } else {
        query = query.is('nature_of_business_id', null);
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  },
  async createCharge(charge) {
    try {
      const payload = { 
        item: charge.item, 
        amount: charge.amount !== '' ? parseFloat(charge.amount) : null,
        application_type_id: charge.application_type_id || null,
        nature_of_business_id: charge.nature_of_business_id || null
      }
      const { data, error } = await supabase
        .from('bplo_charges')
        .insert(payload)
        .select()
      if (error) throw error
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async updateCharge(id, charge) {
    try {
      const payload = { 
        item: charge.item, 
        amount: charge.amount !== '' ? parseFloat(charge.amount) : null,
        application_type_id: charge.application_type_id || null,
        nature_of_business_id: charge.nature_of_business_id || null
      }
      const { data, error } = await supabase
        .from('bplo_charges')
        .update(payload)
        .eq('id', id)
        .select()
      if (error) throw error
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteCharge(id) {
    try {
      const { error } = await supabase
        .from('bplo_charges')
        .delete()
        .eq('id', id)
      if (error) throw error
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async createApplicationType(name, is_active = true) {
    try {
      const { error } = await supabase
        .from('bplo_application_types')
        .insert({ name, is_active })
      if (error) throw error
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async updateApplicationType(id, patch) {
    try {
      const { error } = await supabase
        .from('bplo_application_types')
        .update(patch)
        .eq('id', id)
      if (error) throw error
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async checkNatureUsage(id, name) {
    try {
      // Check default charges (by ID)
      const { count: chargesCount, error: chargesError } = await supabase
        .from('bplo_default_charges')
        .select('*', { count: 'exact', head: true })
        .eq('nature_of_business_id', id);
      
      if (chargesError) throw chargesError;
      if (chargesCount > 0) return { inUse: true, reason: `Used in ${chargesCount} default charge(s)` };

      // Check applications (by Name)
      const { count: appsCount, error: appsError } = await supabase
        .from('bplo_applications')
        .select('*', { count: 'exact', head: true })
        .eq('nature_of_business', name);
        
      if (appsError) throw appsError;
      if (appsCount > 0) return { inUse: true, reason: `Used in ${appsCount} application(s)` };

      return { inUse: false };
    } catch (error) {
      return { inUse: false, error: error.message }; // Fail open or closed? Let's assume not in use if error, but log it.
    }
  },

  async checkAppTypeUsage(id, name) {
    try {
      // Check default charges (by ID)
      const { count: chargesCount, error: chargesError } = await supabase
        .from('bplo_default_charges')
        .select('*', { count: 'exact', head: true })
        .eq('application_type_id', id);
      
      if (chargesError) throw chargesError;
      if (chargesCount > 0) return { inUse: true, reason: `Used in ${chargesCount} default charge(s)` };

      // Check applications (by Name)
      const { count: appsCount, error: appsError } = await supabase
        .from('bplo_applications')
        .select('*', { count: 'exact', head: true })
        .eq('application_type', name);
        
      if (appsError) throw appsError;
      if (appsCount > 0) return { inUse: true, reason: `Used in ${appsCount} application(s)` };

      return { inUse: false };
    } catch (error) {
      return { inUse: false, error: error.message };
    }
  },

  async deleteApplicationType(id) {
    try {
      const { data: existing, error: e0 } = await supabase
        .from('bplo_application_types')
        .select('id')
        .eq('id', id)
        .maybeSingle()
      if (e0) throw e0
      if (!existing) {
        return { success: false, error: 'Delete failed: Item not found.' }
      }
      const { error } = await supabase
        .from('bplo_application_types')
        .delete()
        .eq('id', id)
      if (error) throw error
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async createNatureOfBusiness(name) {
    try {
      const { data, error } = await supabase
        .from('bplo_nature_of_business')
        .insert({ name })
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async updateNatureOfBusiness(id, patch) {
    try {
      const { data, error } = await supabase
        .from('bplo_nature_of_business')
        .update(patch)
        .eq('id', id)
        .select()
      if (error) throw error

      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteNatureOfBusiness(id) {
    try {
      const { error } = await supabase
        .from('bplo_nature_of_business')
        .delete()
        .eq('id', id)
      if (error) throw error
      
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async getDefaultChargesByTypeId(typeId) {
    try {
      const { data, error } = await supabase
        .from('bplo_default_charges')
        .select('*')
        .eq('application_type_id', typeId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async getDefaultChargesByNatureId(natureId) {
    try {
      const { data, error } = await supabase
        .from('bplo_default_charges')
        .select('*')
        .eq('nature_of_business_id', natureId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async getDefaultChargesByTypeName(typeName) {
    try {
      const { data: type, error: tErr } = await supabase
        .from('bplo_application_types')
        .select('id')
        .eq('name', typeName)
        .maybeSingle()
      if (tErr) throw tErr
      const typeId = type?.id
      if (!typeId) return { success: true, data: [] }
      return await this.getDefaultChargesByTypeId(typeId)
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async createDefaultCharge(application_type_id, item, amount, nature_of_business_id = null) {
    try {
      const payload = { item, amount };
      if (application_type_id) payload.application_type_id = application_type_id;
      if (nature_of_business_id) payload.nature_of_business_id = nature_of_business_id;
      
      const { data, error } = await supabase
        .from('bplo_default_charges')
        .insert(payload)
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async updateDefaultCharge(id, patch) {
    try {
      const { data, error } = await supabase
        .from('bplo_default_charges')
        .update(patch)
        .eq('id', id)
        .select()
      if (error) throw error
      if (!data || data.length === 0) {
        return { success: false, error: 'Update failed: Item not found or permission denied.' }
      }
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteDefaultCharge(id) {
    try {
      const { data, error } = await supabase
        .from('bplo_default_charges')
        .delete()
        .eq('id', id)
        .select()
      if (error) throw error
      if (!data || data.length === 0) {
        return { success: false, error: 'Delete failed: Item not found or permission denied.' }
      }
      
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  
  // Fishing Gear Categories
  async getFishingGearCategories() {
    try {
      const { data, error } = await supabase
        .from('bplo_fishing_gear_categories')
        .select('*')
        .order('name', { ascending: true })
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async createFishingGearCategory(name) {
    try {
      const { data, error } = await supabase
        .from('bplo_fishing_gear_categories')
        .insert({ name })
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async updateFishingGearCategory(id, name) {
    try {
      const { data, error } = await supabase
        .from('bplo_fishing_gear_categories')
        .update({ name })
        .eq('id', id)
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteFishingGearCategory(id) {
    try {
      const { error } = await supabase
        .from('bplo_fishing_gear_categories')
        .delete()
        .eq('id', id)
      if (error) throw error
      
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  // Fishing Gears
  async getFishingGears() {
    try {
      // We join with categories to get the category name if needed, 
      // but for now the frontend expects a flat list or we can shape it.
      // The frontend currently expects { id, name, category }
      // So we will select category name via join.
      const { data, error } = await supabase
        .from('bplo_fishing_gears')
        .select(`
          id, 
          name, 
          category_id,
          category:bplo_fishing_gear_categories(name)
        `)
        .order('name', { ascending: true })
      
      if (error) throw error
      
      // Transform data to match frontend expectation: { id, name, category: 'Category Name' }
      const formatted = (data || []).map(g => ({
        id: g.id,
        name: g.name,
        category_id: g.category_id,
        category: g.category?.name || 'Uncategorized'
      }))
      
      return { success: true, data: formatted }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async createFishingGear(name, category_id) {
    try {
      const { data, error } = await supabase
        .from('bplo_fishing_gears')
        .insert({ name, category_id })
        .select()
      if (error) throw error
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async updateFishingGear(id, name, category_id) {
    try {
      const { data, error } = await supabase
        .from('bplo_fishing_gears')
        .update({ name, category_id })
        .eq('id', id)
        .select()
      if (error) throw error
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteFishingGear(id) {
    try {
      const { error } = await supabase
        .from('bplo_fishing_gears')
        .delete()
        .eq('id', id)
      if (error) throw error
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  // Custom Charge Items
  async getCustomChargeItems() {
    try {
      const { data, error } = await supabase
        .from('bplo_custom_charge_items')
        .select('*')
        .order('name', { ascending: true })
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async createCustomChargeItem(name) {
    try {
      const { data, error } = await supabase
        .from('bplo_custom_charge_items')
        .insert({ name })
        .select()
      if (error) throw error
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteCustomChargeItem(id) {
    try {
      const { error } = await supabase
        .from('bplo_custom_charge_items')
        .delete()
        .eq('id', id)
      if (error) throw error
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  // Custom Charge Percentages
  async getCustomChargePercentages() {
    try {
      const { data, error } = await supabase
        .from('bplo_custom_charge_percentages')
        .select('*')
        .order('value', { ascending: true })
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      return { success: false, error: error.message, data: [] }
    }
  },
  async createCustomChargePercentage(name, value) {
    try {
      const { data, error } = await supabase
        .from('bplo_custom_charge_percentages')
        .insert({ name, value })
        .select()
      if (error) throw error
      
      return { success: true, data: data?.[0] }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },
  async deleteCustomChargePercentage(id) {
    try {
      const { error } = await supabase
        .from('bplo_custom_charge_percentages')
        .delete()
        .eq('id', id)
      if (error) throw error
      
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }
}
